/**
 * F-073 — Texas Lead Qualification Dashboard (E-022 FE, Batch-8).
 *
 * ACs: "Lead score calculated from Texas-specific factors", "High-quality
 * Texas leads flagged for outreach", "Texas market segment identified".
 *
 * Reads the EXISTING /api/lead-scoring/leads engine (no new BE routes) and
 * derives the TX-factor breakdown per lead from texasLeadFactors.ts. Rows the
 * E-022 BE has not published yet (SB 38, Subchapter D gaps, municipal
 * registration, budget) render as explicitly pending rather than guessing.
 *
 * Market-gated to texas (renders null otherwise), registered at
 * /texas/lead-qualification (group 'other', URL-reachable).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAppMarket } from '../../services/MarketService';
import { LeadScore, leadScoringApi, currentTenantId } from '../../services/leadScoring';
import {
    texasFactorsFromLead,
    summarizeTexasLeads,
    outreachLeads,
    TexasFactorRow,
} from '../../services/texasLeadFactors';

export const TexasLeadQualification: React.FC = () => {
    const isTexas = getAppMarket() === 'texas';
    const [leads, setLeads] = useState<LeadScore[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<string | null>(null);
    const [surfaces, setSurfaces] = useState<string[]>([]);

    const load = useCallback(() => {
        setLoading(true);
        leadScoringApi
            .getScores()
            .then((rows) => {
                setLeads(rows);
                setError(null);
            })
            .catch((e: any) => setError(e?.message || 'Unable to load Texas leads'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (isTexas) load();
    }, [isTexas, load]);

    const summary = useMemo(() => summarizeTexasLeads(leads), [leads]);
    const outreach = useMemo(() => outreachLeads(leads), [leads]);
    const rows = useMemo(() => leads.map((lead) => ({ lead, factors: texasFactorsFromLead(lead) })), [leads]);
    const surfaced = rows.filter(({ lead }) => surfaces.includes(lead.id));
    const expandedFactors = expanded ? rows.find(({ lead }) => lead.id === expanded)?.factors : undefined;

    const surface = (id: string) =>
        setSurfaces((prev) => (prev.includes(id) ? prev : [...prev, id]));

    if (!isTexas) return null;

    const emptyBecauseTenant = !currentTenantId();

    const factorChip = (row: TexasFactorRow) => (
        <span
            key={row.key}
            data-testid="tx-factor-chip"
            className={`mr-1 inline-block rounded px-1.5 py-0.5 text-xs ${
                row.pending
                    ? 'bg-gray-100 text-gray-400'
                    : row.met
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-amber-100 text-amber-700'
            }`}
            title={row.pending ? 'Awaiting E-022 Texas factor from the lead-scoring engine' : row.label}
        >
            {row.label}
            {!row.pending && <span className="ml-1 font-semibold">{String(row.value)}</span>}
        </span>
    );

    return (
        <div className="mx-auto max-w-6xl px-4 py-8" data-testid="texas-lead-qualification">
            <h1 className="text-2xl font-bold text-gray-900">Texas Lead Qualification</h1>
            <p className="mt-1 text-sm text-gray-600">
                Scored against Texas-specific factors, segmented by portfolio size, and flagged when a lead is ready for
                outreach.
            </p>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <div className="text-xs uppercase text-gray-500">Leads</div>
                    <div className="text-xl font-bold text-gray-900" data-testid="tx-total">
                        {summary.total}
                    </div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <div className="text-xs uppercase text-gray-500">Texas</div>
                    <div className="text-xl font-bold text-gray-900" data-testid="tx-texas-count">
                        {summary.texas}
                    </div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <div className="text-xs uppercase text-gray-500">High quality</div>
                    <div className="text-xl font-bold text-emerald-700" data-testid="tx-high-quality-count">
                        {summary.highQuality}
                    </div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                    <div className="text-xs uppercase text-gray-500">Market segments</div>
                    <div className="text-xl font-bold text-gray-900" data-testid="tx-outreach-count">
                        {outreach.length}
                    </div>
                    <div className="text-xs text-gray-500">ready to surface</div>
                </div>
            </div>

            {loading && (
                <p className="mt-6 text-sm text-gray-600" data-testid="tx-loading">
                    Scoring your Texas leads…
                </p>
            )}

            {error && (
                <p className="mt-6 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="tx-error">
                    {error}
                </p>
            )}

            {!loading && !error && rows.length === 0 && (
                <div className="mt-6 rounded border border-gray-200 bg-white p-6 text-sm text-gray-600" data-testid="tx-empty">
                    {emptyBecauseTenant
                        ? 'No tenant scope is attached to this session yet — sign in with a tenant to see scored leads.'
                        : 'No scored leads yet. The lead-scoring engine publishes leads as Brevo captures arrive.'}
                </div>
            )}

            {rows.length > 0 && (
                <div className="mt-6 overflow-x-auto rounded-lg border border-gray-200 bg-white">
                    <table className="min-w-full text-left text-sm">
                        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                            <tr>
                                <th className="px-3 py-2">Lead</th>
                                <th className="px-3 py-2">Score</th>
                                <th className="px-3 py-2">Texas city</th>
                                <th className="px-3 py-2">Properties</th>
                                <th className="px-3 py-2">Market segment</th>
                                <th className="px-3 py-2">TX factors</th>
                                <th className="px-3 py-2">Outreach</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(({ lead, factors }) => (
                                <tr key={lead.id} className="border-t border-gray-100 align-top" data-testid="tx-lead-row">
                                    <td className="px-3 py-2">
                                        <div className="font-medium text-gray-900" data-testid="tx-lead-email">
                                            {lead.email || lead.name || lead.id}
                                        </div>
                                        <div className="text-xs text-gray-500">{lead.source || '—'}</div>
                                    </td>
                                    <td className="px-3 py-2">
                                        <span
                                            className={`rounded px-2 py-0.5 text-xs font-semibold ${
                                                factors.highQuality
                                                    ? 'bg-emerald-100 text-emerald-700'
                                                    : lead.score >= 40
                                                      ? 'bg-amber-100 text-amber-700'
                                                      : 'bg-gray-100 text-gray-600'
                                            }`}
                                        >
                                            {lead.score}
                                        </span>
                                        {factors.highQuality && (
                                            <span
                                                className="ml-1 rounded bg-emerald-600 px-1.5 py-0.5 text-xs font-bold text-white"
                                                data-testid="tx-high-quality"
                                            >
                                                High quality
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2">{factors.city}</td>
                                    <td className="px-3 py-2">{factors.propertyCount}</td>
                                    <td className="px-3 py-2">
                                        <span
                                            className="rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
                                            data-testid="tx-segment"
                                        >
                                            {factors.segment}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2">
                                        <button
                                            type="button"
                                            className="text-xs font-medium text-blue-600 underline"
                                            data-testid="tx-toggle-factors"
                                            onClick={() => setExpanded(expanded === lead.id ? null : lead.id)}
                                        >
                                            {factors.factors.filter((f) => f.pending).length} pending /{' '}
                                            {factors.factors.length} factors
                                        </button>
                                    </td>
                                    <td className="px-3 py-2">
                                        {surfaces.includes(lead.id) ? (
                                            <span
                                                className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700"
                                                data-testid="tx-surfaced"
                                            >
                                                Surfaced
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                className="rounded bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700"
                                                data-testid="tx-surface-outreach"
                                                onClick={() => surface(lead.id)}
                                            >
                                                Surface for outreach
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {expandedFactors && (
                <div
                    className="mt-4 rounded-lg border border-gray-200 bg-white p-4"
                    data-testid="tx-factor-breakdown"
                >
                    <h2 className="text-sm font-bold text-gray-900">Texas-specific factors</h2>
                    <div className="mt-2">
                        {expandedFactors.factors.map((row) => (
                            <div
                                key={row.key}
                                data-testid="tx-factor-row"
                                className="flex items-baseline justify-between border-b border-gray-100 py-1.5 text-sm"
                            >
                                <span className="text-gray-700">{row.label}</span>
                                <span
                                    className={
                                        row.pending
                                            ? 'text-xs italic text-gray-400'
                                            : row.met
                                              ? 'font-semibold text-emerald-700'
                                              : 'font-semibold text-amber-700'
                                    }
                                >
                                    {row.pending ? 'awaiting E-022 engine' : String(row.value)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {surfaced.length > 0 && (
                <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="tx-outreach-queue">
                    <h2 className="text-sm font-bold text-emerald-800">Queued for outreach</h2>
                    <ul className="mt-1 text-sm text-emerald-800">
                        {surfaced.map(({ lead, factors }) => (
                            <li key={lead.id} data-testid="tx-outreach-lead">
                                {lead.email || lead.id} — {factors.segment} — score {lead.score}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default TexasLeadQualification;
